import React, { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "../lib/api";
import { Bell, Mail, Save, Crown, Sparkles, ChevronRight, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { useBilling } from "../context/BillingContext";
import BrokerConnect from "../components/BrokerConnect";

// Asset-type keys must match server-side classify_instrument()
const ASSET_TYPE_OPTIONS = [
  { key: "babybond",  label: "Babybonds" },
  { key: "preferred", label: "Preferred" },
  { key: "bdc",       label: "BDCs" },
  { key: "reit",      label: "REITs" },
  { key: "etf",       label: "ETFs" },
  { key: "common",    label: "Common" },
  { key: "other",     label: "Other" },
];

export default function Settings() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const { tier, subscription, openUpgrade } = useBilling();

  const loadPrefs = useCallback(async () => {
    try {
      const { data } = await api.get("/preferences");
      setPrefs(data);
    } catch {
      setPrefs({ alert_days_ahead: 7, in_app_enabled: true, email_enabled: false });
    }
  }, []);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/preferences", {
        alert_days_ahead: prefs.alert_days_ahead,
        in_app_enabled: prefs.in_app_enabled,
        email_enabled: prefs.email_enabled,
        risk_email_enabled: prefs.risk_email_enabled,
        risk_muted_tickers: prefs.risk_muted_tickers,
        risk_muted_asset_types: prefs.risk_muted_asset_types,
      });
      setPrefs(data);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) {
    return <div className="min-h-screen bg-champagne p-8 text-graphite/55 font-mono text-sm animate-pulse">Loading preferences...</div>;
  }

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::PREFERENCES</div>
        <h1 className="text-2xl font-bold tracking-tight text-graphite">Notification Settings</h1>
      </div>

      <div className="p-8 max-w-2xl space-y-6">
        <BillingCard tier={tier} subscription={subscription} openUpgrade={openUpgrade} />
        <BrokerConnect variant="card" />

      <div className="space-y-px bg-stone border border-stone">
        <div className="bg-champagne p-6">
          <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold mb-1">ALERT WINDOW</div>
          <div className="text-base font-semibold text-graphite mb-2">Notify me about payments due within</div>
          <div className="text-xs text-graphite/55 mb-4">Coupon payments coming up sooner than this threshold will appear in your alerts banner.</div>
          <div className="grid grid-cols-4 gap-px bg-stone border border-stone">
            {[3, 7, 14, 30].map((d) => (
              <button
                key={d}
                data-testid={`days-${d}`}
                onClick={() => set("alert_days_ahead", d)}
                className={`py-4 text-center font-mono transition-colors ${prefs.alert_days_ahead === d ? "bg-paars text-graphite font-bold" : "bg-champagne text-graphite/80 hover:bg-stone/50"}`}
              >
                <div className="text-xl">{d}</div>
                <div className="text-[10px] uppercase tracking-widest opacity-70">days</div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-champagne p-6">
          <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold mb-4">ALERT CHANNELS</div>

          <Toggle
            icon={Bell}
            label="In-app notifications"
            sub="Show banner on dashboard when payments are due"
            checked={prefs.in_app_enabled}
            onChange={(v) => set("in_app_enabled", v)}
            testid="toggle-in-app"
          />

          <div className="h-px bg-stone my-4" />

          <Toggle
            icon={Mail}
            label="Email digest"
            sub="Weekly summary of upcoming payments (not yet configured — needs Resend API key)"
            checked={prefs.email_enabled}
            onChange={(v) => set("email_enabled", v)}
            testid="toggle-email"
            disabled
          />
        </div>

        <div className="bg-champagne p-6" data-testid="cut-risk-section">
          <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold mb-4">CUT-RISK ALERTS</div>
          <Toggle
            icon={AlertTriangle}
            label="Cut-risk emails"
            sub="HIGH-severity events arrive instantly. MEDIUM/LOW bundled into a daily 09:00 UTC digest."
            checked={prefs.risk_email_enabled ?? true}
            onChange={(v) => set("risk_email_enabled", v)}
            testid="toggle-risk-email"
          />
          <div className="h-px bg-stone my-4" />
          {/* Asset-type mutes */}
          <div className="mb-4">
            <div className="text-xs font-semibold text-graphite mb-2">Mute entire asset types</div>
            <div className="text-[11px] text-graphite/55 mb-3">No emails for any ticker in these categories.</div>
            <div className="flex flex-wrap gap-2">
              {ASSET_TYPE_OPTIONS.map((opt) => {
                const muted = (prefs.risk_muted_asset_types || []).includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    data-testid={`mute-type-${opt.key}`}
                    onClick={() => {
                      const cur = prefs.risk_muted_asset_types || [];
                      set("risk_muted_asset_types", muted ? cur.filter((x) => x !== opt.key) : [...cur, opt.key]);
                    }}
                    className={`px-3 py-1.5 text-[11px] font-mono tracking-wider uppercase border transition-colors ${
                      muted
                        ? "bg-graphite text-champagne border-graphite"
                        : "bg-champagne text-graphite/70 border-stone hover:border-paars-dark hover:text-paars-dark"
                    }`}
                  >
                    {muted ? "× " : "✓ "}{opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Per-ticker mutes */}
          <div>
            <div className="text-xs font-semibold text-graphite mb-2">Mute specific tickers</div>
            <div className="text-[11px] text-graphite/55 mb-3">No emails for these even if you hold or watch them.</div>
            <MutedTickersEditor
              tickers={prefs.risk_muted_tickers || []}
              onChange={(arr) => set("risk_muted_tickers", arr)}
            />
          </div>
        </div>

        <div className="bg-champagne p-6 flex items-center justify-end">
          <button
            data-testid="save-prefs-btn"
            onClick={save}
            disabled={saving}
            className="px-5 py-2.5 bg-paars text-graphite font-semibold text-sm hover:bg-paars-light flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "SAVING..." : "SAVE PREFERENCES"}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

function BillingCard({ tier, subscription, openUpgrade }) {
  const meta =
    tier === "founder_lifetime"
      ? { label: "Founder Lifetime", icon: Crown, accent: "bg-gold", desc: "Pro forever. Thank you for backing the project." }
      : tier === "pro"
      ? { label: "Pro", icon: Sparkles, accent: "bg-paars", desc: subscription?.current_period_end ? `Active until ${fmtDate(subscription.current_period_end)}` : "Active" }
      : { label: "Free", icon: null, accent: "bg-stone", desc: "5 holdings max. No alerts, no realtime." };
  const Icon = meta.icon;
  return (
    <div data-testid="settings-billing-card" className="bg-champagne border border-stone">
      <div className="px-6 py-5 border-b border-stone flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold mb-1">// PLAN</div>
          <div className="text-base font-semibold text-graphite">Your subscription</div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-bold text-graphite ${meta.accent}`}>
          {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />}
          {meta.label}
        </span>
      </div>
      <div className="px-6 py-5 flex items-center justify-between">
        <div className="text-sm text-graphite/65">{meta.desc}</div>
        {tier !== "founder_lifetime" && (
          <button
            data-testid="settings-upgrade-btn"
            onClick={() => openUpgrade()}
            className="px-4 py-2.5 bg-graphite text-champagne text-xs font-semibold tracking-wider uppercase hover:bg-graphite/90 flex items-center gap-2"
          >
            {tier === "pro" ? "Extend / change plan" : "Upgrade to Pro"} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function Toggle({ icon: Icon, label, sub, checked, onChange, testid, disabled }) {
  return (
    <div className="flex items-start gap-4 py-3">
      <Icon className={`w-5 h-5 mt-0.5 ${disabled ? "text-graphite/35" : "text-paars-dark"}`} strokeWidth={1.5} />
      <div className="flex-1">
        <div className={`text-sm font-semibold ${disabled ? "text-graphite/55" : "text-graphite"}`}>{label}</div>
        <div className="text-xs text-graphite/55 mt-0.5">{sub}</div>
      </div>
      <button
        data-testid={testid}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative w-12 h-6 transition-colors ${checked ? "bg-paars" : "bg-stone"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white shadow transition-all ${checked ? "left-[26px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}


function MutedTickersEditor({ tickers, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const t = input.trim().toUpperCase();
    if (!t) return;
    if (tickers.includes(t)) {
      setInput("");
      return;
    }
    onChange([...tickers, t]);
    setInput("");
  };
  return (
    <div data-testid="muted-tickers-editor">
      <div className="flex items-center gap-2 mb-3">
        <input
          data-testid="mute-ticker-input"
          value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Ticker, e.g. PSEC"
          className="flex-1 px-3 py-2 border border-stone bg-champagne text-sm font-mono uppercase focus:outline-none focus:border-paars-dark"
        />
        <button
          data-testid="mute-ticker-add"
          onClick={add}
          disabled={!input.trim()}
          className="px-3 py-2 border border-paars-dark text-paars-dark text-xs font-semibold tracking-widest uppercase hover:bg-paars/30 disabled:opacity-40"
        >
          Mute
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {tickers.length === 0 && (
          <div className="text-[11px] text-graphite/45 font-mono italic">No tickers muted.</div>
        )}
        {tickers.map((t) => (
          <span
            key={t}
            data-testid={`muted-ticker-${t}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-stone/60 text-graphite/80 text-[11px] font-mono tracking-wider"
          >
            {t}
            <button
              data-testid={`muted-ticker-remove-${t}`}
              onClick={() => onChange(tickers.filter((x) => x !== t))}
              className="text-graphite/45 hover:text-burgundy"
              title="Unmute"
            >
              <X className="w-3 h-3" strokeWidth={2.5} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}
